/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { B2bPermission, isB2bActionPermitted } from '@msdyn365-commerce-modules/retail-actions';
import { format, getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IAccountInvoiceTileData } from './account-invoice-tile.data';
import { IAccountInvoiceTileProps } from './account-invoice-tile.props.autogenerated';
import { AccountInvoiceTileDescription } from './components';
import LinksComponent from './components/account-invoice-tile-links';

export interface IAccountInvoiceTileViewProps extends IAccountInvoiceTileProps<IAccountInvoiceTileData> {
    AccountInvoiceTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
    description?: React.ReactNode;
}

/**
 *
 * AccountInvoiceTile component.
 * @extends {React.PureComponent<IAccountInvoiceTileProps<IAccountInvoiceTileData>>}
 */
class AccountInvoiceTile extends React.PureComponent<IAccountInvoiceTileProps<IAccountInvoiceTileData>> {
    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IAccountInvoiceTileProps<IAccountInvoiceTileData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public render(): JSX.Element | null {
        const {
            config,
            data,
            resources,
            context: {
                cultureFormatter: {
                    formatCurrency,

                    // @ts-expect-error
                    currencyCode
                }
            }
        } = this.props;

        // Viewing invoices requires the AllowViewInvoices permission; when the B2BContact permission model
        // is off this falls back to legacy behavior (visible to all B2B users). Gating the tile here keeps
        // the "My Invoices" entry point in sync with the invoices-list page, which returns null without
        // the permission (avoiding a link that leads to a blank page).
        const canViewInvoices = isB2bActionPermitted({
            isB2bContact: this.props.context.request.user.isB2bContact,
            isLegacyAdmin: true,
            permissions: data.businessPartnerUser?.result?.Permissions,
            required: B2bPermission.AllowViewInvoices
        });
        if (!canViewInvoices) {
            return null;
        }

        const { className, heading, links } = config;
        const { accountInvoiceTileDescription } = resources;

        const openinvoiceAmount = data.customerBalance.result?.OpenInvoiceBalance || 0;
        const invoiceTotalAmount: string | undefined = formatCurrency(openinvoiceAmount.toFixed(2), currencyCode);

        const description = invoiceTotalAmount && format(accountInvoiceTileDescription, invoiceTotalAmount);

        const viewProps = {
            ...this.props,
            className,
            AccountInvoiceTile: {
                moduleProps: this.props,
                className: classnames('ms-account-invoice-tile', config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className='ms-account-invoice-tile__heading'
                    tag={heading.tag || 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: links && links.length > 0 && (
                <LinksComponent
                    {...{
                        links,
                        onTextChange: this.handleLinkTextChange,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent
                    }}
                />
            ),
            description: description && <AccountInvoiceTileDescription description={description} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default AccountInvoiceTile;
